import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { FetchTargetBadge_fetchTargetBadge } from "_graphql/queries/__generated__/fetchTargetBadge";
import { FC, useState } from "react";

interface TargetBadgeViewProps {
  badge: FetchTargetBadge_fetchTargetBadge | null;
}

const TargetBadgeView: FC<TargetBadgeViewProps> = ({ badge }) => {
  const [expandedGift, setExpandedGift] = useState<number | null>(null);

  if (!badge) return <div>No badge data available</div>;

  const toggleGift = (idx: number) => {
    setExpandedGift(expandedGift === idx ? null : idx);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Badge Information</h3>
        <div className="mt-2 grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-500">Badge Name</p>
            <p className="mt-1 text-sm text-gray-900">{badge.name}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Description</p>
            <p className="mt-1 text-sm text-gray-900">
              {badge.description || "N/A"}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Points Range</p>
            <p className="mt-1 text-sm text-gray-900">
              {badge.min_points || 0} - {badge.max_points || 0}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-500">Cash Equivalent</p>
            <p className="mt-1 text-sm text-gray-900">
              ₵{badge.minimum_cash_equivalent || 0} - ₵
              {badge.cash_equivalent || 0}
            </p>
          </div>
        </div>
      </div>

      {badge.gift_basket?.length ? (
        <div>
          <h3 className="text-lg font-medium text-gray-900">Gift Basket</h3>
          <div className="mt-2 space-y-2">
            {badge.gift_basket.map((gift, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-md overflow-hidden"
              >
                <button
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                  onClick={() => toggleGift(idx)}
                >
                  <div className="flex items-center">
                    <span className="font-medium text-gray-900">
                      {gift?.gift_name}
                    </span>
                    <span className="ml-2 text-sm text-gray-500">
                      ({gift?.gift_type})
                    </span>
                  </div>
                  {expandedGift === idx ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </button>

                {expandedGift === idx && (
                  <div className="p-4 bg-white">
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">
                          Description
                        </p>
                        <p className="mt-1 text-sm text-gray-900">
                          {gift?.gift_description}
                        </p>
                      </div>

                      {gift?.gift_image && (
                        <div>
                          <p className="text-sm font-medium text-gray-500">
                            Gift Image
                          </p>
                          <div className="mt-2 flex justify-center">
                            <img
                              src={gift.gift_image}
                              alt={gift.gift_name}
                              className="max-w-full h-auto max-h-40 w-auto rounded-md shadow-sm object-contain"
                              style={{ maxWidth: "200px" }}
                              onError={(e) => {
                                // Fallback for broken images
                                const target = e.target as HTMLImageElement;
                                target.src =
                                  "https://via.placeholder.com/200x150?text=Image+Unavailable";
                                target.alt = "Image could not be loaded";
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default TargetBadgeView;
